import { ZoomAndNavigatePdfPanel } from '@repo/atomic-components';
import { ThreeColumnPdfTemplate } from '@/ui/templates';
import { AccessControlAction } from '@/constants';
import { useAccessControl } from '@/hooks';
import {
  ActionsPanel,
  BundleSidebarTabs,
  ChronologyEntryPanel,
  GridContainer,
  FilterAnnotationLabelsControlPanel,
  FilterChips,
  NavigationAndInteractionControlPanel,
  ReactVirtualizedList,
  RedactPagePanel,
  RelatedPagesPanel,
} from './components';
import { useAnnotationLabels, useViewportPageIndex } from './hooks';
import { ViewportPageIndexContext } from './ViewportPageIndexContext';
import { AnnotationLabelsContext } from './AnnotationLabelsContext';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  useLazyGetBundleFilterOptionsQuery,
  useLazyGetBundleQuery,
  useLazyGetBundleSectionsSuggestionsCategoryListQuery,
  useLazyGetBundleSectionsSuggestionsSubCategoryListQuery,
  useLazyGetBundleSectionsQuery,
  useLazyGetBundleUndoRedoHistoryQuery,
  useRotatePageMutation,
} from '@/api';
import {
  makeSelectBundlePageDataByPageIndex,
  makeSelectBundlePageIndexByPageId,
  selectPageInfoCache,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  selectSharedDataInitialPageId,
  selectViewportPageIndexInStore,
  setBundlePageScaleZoomIn,
  setBundlePageScaleZoomOut,
  setSharedDataInitialPageId,
  setViewportPageIndexInStore,
  useAppDispatch,
  useAppSelector,
} from '@/store';

// How long the panes get to land on `sharedDataInitialPageId` once the data they
// need is in, before the page drops it.
const INITIAL_PAGE_TARGET_LIFETIME_MS = 5000;

// The Redact button is temporarily hidden; set to true to bring it back.
const IS_REDACT_PAGE_ENABLED = false;

export const DocumentReviewPage: FC = () => {
  const dispatch = useAppDispatch();

  const bundleId = useAppSelector(selectSharedDataBundleId);
  const sharedDataInitialPageId = useAppSelector(selectSharedDataInitialPageId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const getBundlePageIndexByPageId = useAppSelector(makeSelectBundlePageIndexByPageId);
  const getBundlePageDataByPageIndex = useAppSelector(makeSelectBundlePageDataByPageIndex);
  const viewportPageIndex = useAppSelector(selectViewportPageIndexInStore);
  const pageInfoCache = useAppSelector(selectPageInfoCache);
  const isEditBundles = useAccessControl(AccessControlAction.EDIT_BUNDLES);

  // Chronology entries on the currently-visible page — drives the count badge on
  // the Add-to-Chronology panel, mirroring the thumbnail corner indicator. Prefer
  // the lazily-loaded cache (which reflects optimistic updates from the
  // AddToChronology dialog) and fall back to the bundle data map.
  const currentViewportPageData = getBundlePageDataByPageIndex(viewportPageIndex);
  const currentPageChronologyEntryIds = currentViewportPageData?.pageId
    ? (pageInfoCache[currentViewportPageData.pageId]?.chronologyEntryIds ?? currentViewportPageData?.chronologyEntryIds)
    : undefined;

  const viewportPageIndexProps = useViewportPageIndex();
  const annotationLabelsProps = useAnnotationLabels();

  const [triggerGetBundle, { isLoading: isBundleLoading, isSuccess: isBundleSuccess, isError: isBundleError }] =
    useLazyGetBundleQuery();
  const [
    triggerGetBundleSections,
    { isLoading: isBundleSectionsLoading, isSuccess: isBundleSectionsSuccess, isError: isBundleSectionsError },
  ] = useLazyGetBundleSectionsQuery();
  const [
    triggerGetBundleFilterOptions,
    { isSuccess: isBundleFilterOptionsSuccess, isError: isBundleFilterOptionsError },
  ] = useLazyGetBundleFilterOptionsQuery();
  const [
    triggerGetBundleSectionsSuggestionsCategoryList,
    {
      isLoading: isBundleSectionsSuggestionsCategoryListLoading,
      isSuccess: isBundleSectionsSuggestionsCategoryListSuccess,
      isError: isBundleSectionsSuggestionsCategoryListError,
    },
  ] = useLazyGetBundleSectionsSuggestionsCategoryListQuery();
  const [
    triggerGetBundleSectionsSuggestionsSubCategoryList,
    {
      isLoading: isBundleSectionsSuggestionsSubCategoryListLoading,
      isSuccess: isBundleSectionsSuggestionsSubCategoryListSuccess,
      isError: isBundleSectionsSuggestionsSubCategoryListError,
    },
  ] = useLazyGetBundleSectionsSuggestionsSubCategoryListQuery();
  const [triggerGetBundleUndoRedoHistory] = useLazyGetBundleUndoRedoHistoryQuery();
  const [triggerRotatePage] = useRotatePageMutation();

  const isError =
    isBundleError ||
    isBundleSectionsError ||
    isBundleFilterOptionsError ||
    isBundleSectionsSuggestionsCategoryListError ||
    isBundleSectionsSuggestionsSubCategoryListError;

  useEffect(() => {
    if (bundleId) {
      triggerGetBundle({ bundleId }, true);
      triggerGetBundleSections({ bundleId });
      triggerGetBundleFilterOptions({ bundleId }, true);
      triggerGetBundleSectionsSuggestionsCategoryList({ bundleId });
      triggerGetBundleSectionsSuggestionsSubCategoryList({ bundleId });
      triggerGetBundleUndoRedoHistory({ bundleId });
    }
  }, [
    bundleId,
    triggerGetBundle,
    triggerGetBundleFilterOptions,
    triggerGetBundleSections,
    triggerGetBundleSectionsSuggestionsCategoryList,
    triggerGetBundleSectionsSuggestionsSubCategoryList,
    triggerGetBundleUndoRedoHistory,
  ]);

  /**
   * The landing target deliberately outlives this effect: the thumbnails rail and
   * the page list each mount and settle on their own schedule, and whichever one
   * is late needs to still find the target when it gets there. Each of them
   * honours it at most once, and this page retires it below. This effect must
   * therefore apply the index only once too, or a later re-run (a new
   * `getBundlePageIndexByPageId` identity after a rotate or refetch) would drag
   * the user back to the landing page.
   */
  const [hasAppliedInitialPageIndex, setHasAppliedInitialPageIndex] = useState(false);

  useEffect(() => {
    if (
      !isError &&
      sharedDataInitialPageId &&
      !hasAppliedInitialPageIndex &&
      isBundleSuccess &&
      isBundleSectionsSuccess &&
      isBundleFilterOptionsSuccess &&
      isBundleSectionsSuggestionsCategoryListSuccess &&
      isBundleSectionsSuggestionsSubCategoryListSuccess
    ) {
      const pageIndex = getBundlePageIndexByPageId(sharedDataInitialPageId);
      setHasAppliedInitialPageIndex(true);

      /**
       * Set init viewportPageIndex for components to init index
       */
      dispatch(setViewportPageIndexInStore(pageIndex === -1 ? 0 : pageIndex));
    }
  }, [
    isError,
    isBundleSuccess,
    isBundleSectionsSuccess,
    isBundleFilterOptionsSuccess,
    isBundleSectionsSuggestionsCategoryListSuccess,
    isBundleSectionsSuggestionsSubCategoryListSuccess,
    sharedDataInitialPageId,
    hasAppliedInitialPageIndex,
    getBundlePageIndexByPageId,
    dispatch,
  ]);

  /**
   * Backstop for the landing target. Both panes latch it as soon as they land, but
   * a pane that never mounts or never gets a measurable width — the medical-match
   * tab is active, the middle column is collapsed — can't, and the target is global.
   * Left set, it would move a pane much later, e.g. when a filter change finally
   * brings that page into the grid. Retire it a beat after the data both panes need
   * has arrived (or the load has failed), which is as long as landing can take.
   */
  useEffect(() => {
    if (!sharedDataInitialPageId || !(hasAppliedInitialPageIndex || isError)) {
      return;
    }

    const timeoutId = setTimeout(() => {
      dispatch(setSharedDataInitialPageId(null));
    }, INITIAL_PAGE_TARGET_LIFETIME_MS);

    return () => clearTimeout(timeoutId);
  }, [sharedDataInitialPageId, hasAppliedInitialPageIndex, isError, dispatch]);

  const pageZoomIn = useCallback(() => {
    if (activeBundleType) {
      dispatch(setBundlePageScaleZoomIn({ key: activeBundleType }));
    }
  }, [activeBundleType, dispatch]);

  const pageZoomOut = useCallback(() => {
    if (activeBundleType) {
      dispatch(setBundlePageScaleZoomOut({ key: activeBundleType }));
    }
  }, [activeBundleType, dispatch]);

  // Redirect the browser's zoom keyboard shortcuts (Ctrl/Cmd +, =, - and the
  // numeric-keypad variants) to the PDF page zoom instead. Without this,
  // Ctrl+= zooms the whole app UI including chrome, which is almost never
  // what a user reviewing a bundle wants. The listener lives on window rather
  // than a specific element so it works regardless of which pane is focused.
  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      // Holding the key would otherwise fire ~20 auto-repeat events per
      // second, racing zoom to its min/max in a moment and forcing pdf.js
      // to re-render every intermediate step. One zoom per keypress.
      if (event.repeat) return;
      // event.key covers "+", "=" and "-" from both the main row and (in most
      // browsers) the numeric keypad; event.code catches keypad variants on
      // browsers that don't normalise.
      const zoomInKey = event.key === '+' || event.key === '=' || event.code === 'NumpadAdd';
      const zoomOutKey = event.key === '-' || event.code === 'NumpadSubtract';
      if (!zoomInKey && !zoomOutKey) return;
      event.preventDefault();
      if (zoomInKey) pageZoomIn();
      else pageZoomOut();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [pageZoomIn, pageZoomOut]);

  const rotateCurrentPage = useCallback(
    (direction: 'clockwise' | 'anticlockwise') => {
      if (!bundleId) {
        return;
      }
      const currentPage = getBundlePageDataByPageIndex(viewportPageIndex);
      if (!currentPage?.pageId) {
        return;
      }
      triggerRotatePage({ bundleId, pageId: currentPage.pageId, direction });
    },
    [bundleId, getBundlePageDataByPageIndex, viewportPageIndex, triggerRotatePage],
  );

  const rotateClockwise = useCallback(() => rotateCurrentPage('clockwise'), [rotateCurrentPage]);
  const rotateAnticlockwise = useCallback(() => rotateCurrentPage('anticlockwise'), [rotateCurrentPage]);

  const navigatePageUp = useCallback(() => {
    if (
      viewportPageIndexProps.pageNumberInputRef.current &&
      viewportPageIndexProps.pageNumberInputRef.current.value &&
      parseInt(viewportPageIndexProps.pageNumberInputRef.current.value) > 0
    ) {
      viewportPageIndexProps.onScrollToPageIndexInReactVirtualizedViewport(
        parseInt(viewportPageIndexProps.pageNumberInputRef.current.value) - 2,
      );
    }
  }, [viewportPageIndexProps]);

  const navigatePageDown = useCallback(() => {
    if (
      viewportPageIndexProps.pageNumberInputRef.current &&
      viewportPageIndexProps.pageNumberInputRef.current.value &&
      parseInt(viewportPageIndexProps.pageNumberInputRef.current.value) > 0
    ) {
      viewportPageIndexProps.onScrollToPageIndexInReactVirtualizedViewport(
        parseInt(viewportPageIndexProps.pageNumberInputRef.current.value),
      );
    }
  }, [viewportPageIndexProps]);

  // Arrow up/down navigate between PDF pages, but only when the user isn't
  // typing or interacting with a form control. Skip if the focused element
  // is an input, textarea, select, or anything marked contentEditable — that
  // covers "Add to Chronology" text areas, filter inputs, page-number
  // inputs, dropdowns, and any rich-text editors elsewhere in the app.
  // Also skip when a component upstream has already handled the key (e.g.
  // the thumbnail grid moving its selection). Dialogs are intentionally NOT
  // guarded here: users compose a chronology entry while flipping through
  // pages to pull text from different pages into the same entry, so arrows
  // must keep navigating the viewer behind the open dialog.
  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      // Don't hijack when a modifier is held — Cmd+Up/Down and similar are
      // system-level shortcuts users may still expect.
      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      if (event.defaultPrevented) return;

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (target?.isContentEditable) return;

      event.preventDefault();
      if (event.key === 'ArrowUp') navigatePageUp();
      else navigatePageDown();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [navigatePageUp, navigatePageDown]);

  return (
    <ViewportPageIndexContext.Provider
      value={{
        ...viewportPageIndexProps,
      }}
    >
      <AnnotationLabelsContext.Provider value={annotationLabelsProps}>
        <ThreeColumnPdfTemplate
          isDataLoading={
            isBundleLoading ||
            isBundleSectionsLoading ||
            // isBundleFilterOptionsLoading || -- Unblocked to allow viewing bundle while filters load
            isBundleSectionsSuggestionsCategoryListLoading ||
            isBundleSectionsSuggestionsSubCategoryListLoading ||
            isError
          }
          actionsPanel={<ActionsPanel />}
          filterChipsPanel={<FilterChips />}
          contentLeftColumn={
            <div className="bg-fletchers-gray-50 border-fletchers-gray-110 h-full w-full overflow-auto border-r">
              <BundleSidebarTabs />
            </div>
          }
          // contentMiddleColumn={<GroupedVirtuosoThumbnailsList />}
          contentMiddleColumn={<GridContainer />}
          contentRightColumn={
            <>
              <FilterAnnotationLabelsControlPanel />

              <ZoomAndNavigatePdfPanel
                zoomIn={pageZoomIn}
                zoomOut={pageZoomOut}
                rotateClockwise={rotateClockwise}
                rotateAnticlockwise={rotateAnticlockwise}
                pageUp={navigatePageUp}
                pageDown={navigatePageDown}
                chronologySlot={
                  <ChronologyEntryPanel
                    pageId={currentViewportPageData?.pageId ?? null}
                    entryCount={currentPageChronologyEntryIds?.length ?? 0}
                    onAddNewEntry={() =>
                      viewportPageIndexProps.reactVirtualizedRef.current?.openChronologyForCurrentPage()
                    }
                  />
                }
                bottomSlot={<RelatedPagesPanel />}
                footerSlot={IS_REDACT_PAGE_ENABLED && isEditBundles ? <RedactPagePanel /> : undefined}
              />

              <NavigationAndInteractionControlPanel />

              <ReactVirtualizedList />
            </>
          }
        />
      </AnnotationLabelsContext.Provider>
    </ViewportPageIndexContext.Provider>
  );
};
